import { type Static, Type } from "@sinclair/typebox"
import { CursorQuerySchema, createPageSchema, IsoDateTimeSchema, Nullable } from "./common"

// Reglas de alarma por vehículo. La fuente de verdad vive aquí (seguridad) y cada nodo
// del gateway las recibe en su proyección ya resueltas por IMEI.

export const AlarmRuleSeveritySchema = Type.Union([
  Type.Literal("critical"),
  Type.Literal("high"),
  Type.Literal("normal"),
  Type.Literal("low"),
])

/** A quién alcanza la regla: vehículos sueltos y/o grupos completos. */
export const AlarmRuleTargetsSchema = Type.Object({
  vehicle_ids: Type.Array(Type.String()),
  group_ids: Type.Array(Type.String()),
})
export type AlarmRuleTargets = Static<typeof AlarmRuleTargetsSchema>

export const AlarmRuleSchema = Type.Object({
  id: Type.String(),
  domain_id: Type.String(),
  name: Type.String(),
  detector_key: Type.String(),
  enabled: Type.Boolean(),
  params: Type.Record(Type.String(), Type.Unknown()),
  samples: Type.Integer(),
  severity: Nullable(AlarmRuleSeveritySchema),
  cooldown_seconds: Nullable(Type.Integer()),
  priority: Type.Integer(),
  targets: AlarmRuleTargetsSchema,
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
})
export type AlarmRule = Static<typeof AlarmRuleSchema>

export const AlarmRulePageSchema = createPageSchema(AlarmRuleSchema)

export const ListAlarmRulesQuerySchema = Type.Composite([
  CursorQuerySchema,
  Type.Object({
    domain: Type.Optional(Type.String()),
    detector_key: Type.Optional(Type.String()),
    /** Reglas que alcanzan a este vehículo (directas o por alguno de sus grupos). */
    vehicle_id: Type.Optional(Type.String()),
  }),
])
export type ListAlarmRulesQuery = Static<typeof ListAlarmRulesQuerySchema>

export const CreateAlarmRuleRequestSchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 120 }),
  detector_key: Type.String({ minLength: 1, maxLength: 60 }),
  enabled: Type.Optional(Type.Boolean()),
  params: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
  samples: Type.Optional(Type.Integer({ minimum: 1, maximum: 20 })),
  severity: Type.Optional(Nullable(AlarmRuleSeveritySchema)),
  cooldown_seconds: Type.Optional(Nullable(Type.Integer({ minimum: 0 }))),
  priority: Type.Optional(Type.Integer()),
  vehicle_ids: Type.Optional(Type.Array(Type.String())),
  group_ids: Type.Optional(Type.Array(Type.String())),
  /** Solo para actores cross-tenant: dominio destino. */
  domain: Type.Optional(Type.String()),
})
export type CreateAlarmRuleRequest = Static<typeof CreateAlarmRuleRequestSchema>

export const UpdateAlarmRuleRequestSchema = Type.Partial(
  Type.Omit(CreateAlarmRuleRequestSchema, ["domain"]),
)
export type UpdateAlarmRuleRequest = Static<typeof UpdateAlarmRuleRequestSchema>

export const SetAlarmRuleTargetsRequestSchema = Type.Object({
  vehicle_ids: Type.Array(Type.String()),
  group_ids: Type.Array(Type.String()),
})
export type SetAlarmRuleTargetsRequest = Static<typeof SetAlarmRuleTargetsRequestSchema>

// --- Proyección al nodo -----------------------------------------------------------
// El nodo recibe el GANADOR ya resuelto por (imei, detector): la precedencia
// (vehículo > grupo > tenant) se calcula UNA vez, aquí. Si la resolviera cada nodo, dos
// versiones distintas podrían discrepar y el fallo seria invisible.

export const AlarmRuleProjectionSchema = Type.Object({
  rule_id: Type.String(),
  domain_slug: Type.String(),
  detector_key: Type.String(),
  enabled: Type.Boolean(),
  params: Type.Record(Type.String(), Type.Unknown()),
  samples: Type.Integer(),
  severity: Nullable(AlarmRuleSeveritySchema),
  cooldown_seconds: Nullable(Type.Integer()),
  /** De dónde salió el ganador; solo para diagnóstico en el nodo. */
  source: Type.Union([Type.Literal("vehicle"), Type.Literal("group")]),
  /** IMEIs alcanzados que viven en ESTE nodo. */
  imeis: Type.Array(Type.String()),
})
export type AlarmRuleProjection = Static<typeof AlarmRuleProjectionSchema>
